// ============================================================================
// file-system-errors.js — DEC-COUNCIL-007 / Candidate C5 Slice 2
// ----------------------------------------------------------------------------
// Single source of truth for the File System Access API transient-error
// classification. Previously this lived in two places:
//
//   - src/services/file-system.js   — `isTransientFileSystemWriteError`
//   - src/services/sync-folder.js   — `isTransientFileSystemError`
//
// The two were structurally similar but NOT byte-for-byte identical:
// sync-folder.js's version had an extra `err?.qmTransientFileSystem === true`
// short-circuit that consumes a writer-side tag set by file-system.js. The
// pair is deliberate coupling, NOT duplication.
//
// This module consolidates them with the augmented logic (the sync-folder
// variant — strictly more permissive). The writer-side tag remains: writers
// in file-system.js (and any other File System Access API caller) MUST
// continue to set `err.qmTransientFileSystem = isTransientFileSystemError(err)`
// before re-throwing, so retry-aware consumers can short-circuit cheaply.
//
// Class of error this catches:
//   - Chrome's "state cached in another tab" race (most common).
//   - Folder picker handle that has changed underneath us.
//   - NotFoundError from a deleted/moved file.
//   - Generic InvalidStateError from FSA primitives.
//
// See DEC-COUNCIL-007 Tsunade's dissent (which surfaced that the pair was
// coupled, not duplicated) — preserving the writer-side tag is the load-
// bearing invariant for this consolidation.
// ============================================================================

/**
 * Returns true if the error looks like a transient File System Access API
 * failure (i.e., the caller should retry once after a short delay).
 *
 * Short-circuits if `err.qmTransientFileSystem === true` so callers that
 * already classified upstream don't pay the substring-scan cost twice.
 *
 * @param {unknown} err
 * @returns {boolean}
 */
export function isTransientFileSystemError(err: unknown): boolean {
    if (err == null) return false;
    if ((err as { qmTransientFileSystem?: unknown }).qmTransientFileSystem === true) return true;
    const message = String((err as { message?: unknown }).message || (err as { name?: unknown }).name || '').toLowerCase();
    return (
        message.includes('state cached')
        || message.includes('state had changed')
        || message.includes('could not be found at the time')
        || message.includes('operation was processed')
        || message.includes('invalidstateerror')
        || (err as { name?: unknown }).name === 'NotFoundError'
    );
}

/**
 * Tag the error with `qmTransientFileSystem` and return it. Writers (any
 * call site that catches FSA errors before re-throwing) MUST tag so retry
 * decisions downstream can short-circuit. Convenience wrapper.
 *
 * @template T
 * @param {T} err
 * @returns {T}
 */
export function tagTransientFileSystemError<T>(err: T): T {
    if (err && typeof err === 'object') {
        // eslint-disable-next-line no-param-reassign
        (err as { qmTransientFileSystem?: unknown }).qmTransientFileSystem = isTransientFileSystemError(err);
    }
    return err;
}
